import { useEffect, useRef } from "react";
import { useTourNavigation, useTourState, useTourStoreApi } from "@heroncendre/player";

/**
 * ============================================================================
 *  UrlScene.tsx — Chaque scène de la visite libre a son adresse
 * ----------------------------------------------------------------------------
 *  La scène courante vit dans l'URL (`?scene=<id>`) : copier l'adresse la
 *  partage, rafraîchir la page reste où l'on était, et le bouton « précédent »
 *  du navigateur ramène à la scène d'avant. Le paramètre est LU au démarrage
 *  (`App.tsx`, qui en fait la scène d'entrée et saute l'accueil) et ÉCRIT ici.
 *
 *  ── Une entrée d'historique par scène visitée ──────────────────────────────
 *
 *  Chaque changement de scène EMPILE une entrée (`pushState`) : « précédent »
 *  revient donc à la scène précédente, « suivant » la rejoue. À l'inverse,
 *  un changement de MODE (accueil → visite libre, retour à l'accueil) REMPLACE
 *  l'entrée courante : y entrer ou en sortir n'est pas un déplacement dans la
 *  visite, et ne doit pas laisser dans l'historique une adresse sans scène où
 *  « précédent » ne ferait rien d'utile.
 *
 *  ── Le sens inverse ────────────────────────────────────────────────────────
 *
 *  Quand le navigateur change l'adresse (`popstate`), c'est la visite qui
 *  suit : on navigue vers la scène demandée. Le changement de scène qui en
 *  résulte ne doit PAS empiler à son tour — l'adresse est déjà la bonne, ce que
 *  la comparaison cible/actuelle garantit, et sans quoi « précédent » ferait
 *  repartir en avant à chaque pas.
 *
 *  ── Hors visite libre ─────────────────────────────────────────────────────
 *
 *  À l'accueil et en parcours narratif, le paramètre est RETIRÉ : le parcours
 *  conduit seul la scène, et rafraîchir l'accueil doit rester l'accueil. Les
 *  autres paramètres (`?live`, `?libre`, `?edit`…) et le fragment (`#debug`)
 *  sont conservés tels quels.
 *
 *  Ne rend rien.
 * ==========================================================================*/

/** Le paramètre d'adresse qui porte la scène. */
export const PARAM_SCENE = "scene";

/** L'adresse (chemin + recherche + fragment) avec `scene` posé ou retiré. */
function adresse(sceneId: string | null): { cible: string; actuelle: string } {
  // La chaîne est réécrite À LA MAIN : `URLSearchParams` réécrit `?edit` en
  // `?edit=`, ce qui est inoffensif mais défigure l'adresse que l'on partage.
  // Seul `scene` est touché, le reste est gardé tel quel, dans l'ordre.
  const autres = window.location.search
    .replace(/^\?/, "")
    .split("&")
    .filter((morceau) => morceau !== "" && morceau.split("=")[0] !== PARAM_SCENE);
  if (sceneId !== null) autres.push(`${PARAM_SCENE}=${encodeURIComponent(sceneId)}`);

  const { pathname, search, hash } = window.location;
  return {
    cible: `${pathname}${autres.length > 0 ? `?${autres.join("&")}` : ""}${hash}`,
    actuelle: `${pathname}${search}${hash}`,
  };
}

export function UrlScene({ actif }: { actif: boolean }) {
  const store = useTourStoreApi();
  const navigation = useTourNavigation();
  const sceneId = useTourState((state) => state.currentSceneId);

  /** Ce qu'on a écrit en dernier : sert à distinguer changement de mode et de scène. */
  const precedent = useRef<{ actif: boolean } | null>(null);

  // Navigateur → visite : « précédent » / « suivant ».
  useEffect(() => {
    if (!actif) return;

    const onPopState = () => {
      const demandee = new URLSearchParams(window.location.search).get(PARAM_SCENE);
      if (demandee === null) return;
      const state = store.getState();
      if (demandee === state.currentSceneId) return;
      // Un identifiant que le document ne connaît pas (lien périmé) : on laisse.
      if (!state.tour.scenes.some((scene) => scene.id === demandee)) return;
      navigation.navigate(demandee);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [actif, store, navigation]);

  // Visite → navigateur.
  useEffect(() => {
    const { cible, actuelle } = adresse(actif ? sceneId : null);
    const changementDeMode = precedent.current === null || precedent.current.actif !== actif;
    precedent.current = { actif };

    if (cible === actuelle) return;

    try {
      if (changementDeMode) window.history.replaceState(window.history.state, "", cible);
      else window.history.pushState(window.history.state, "", cible);
    } catch {
      // Contexte sans historique (cadre isolé) : l'adresse ne suit pas, la
      // visite continue.
    }
  }, [actif, sceneId]);

  return null;
}
